/**
 * Normalizes text for search comparison by:
 * 1. Removing special characters (spaces, hyphens, punctuation, etc.)
 * 2. Normalizing accented characters to ASCII equivalents
 * 3. Converting to lowercase
 * 
 * This allows users to find units with special characters or accents
 * by typing with a standard English keyboard.
 * 
 * @param text The text to normalize
 * @returns Normalized text suitable for search comparison
 * 
 * @example
 * normalizeSearchText("Jäger-Bat. 126") // "jagerbat126"
 * normalizeSearchText("Generální štáb") // "generalnistab"
 * normalizeSearchText("11,43mm") // "1143mm"
 */
export function normalizeSearchText(text: string): string {
  // First, handle special character mappings that don't normalize well
  const specialCharMap: { [key: string]: string } = {
    'ß': 'ss',
    'Ł': 'L',
    'ł': 'l',
  };

  // Apply special character mappings
  let normalized = text
    .split('')
    .map((char) => specialCharMap[char] || char)
    .join('');

  // Normalize Unicode characters (NFD = Normalization Form Decomposed)
  // This separates base characters from their diacritical marks
  normalized = normalized.normalize('NFD');

  // Remove diacritical marks (combining characters in the range \u0300-\u036f)
  normalized = normalized.replace(/[\u0300-\u036f]/g, '');

  // Remove special characters: spaces, hyphens, commas, periods, slashes, hash, etc.
  // This regex matches common punctuation and special characters
  normalized = normalized.replace(/[.,\s\-/#!$%^&*()[\]{}]/g, '');

  // Convert to lowercase for case-insensitive matching
  normalized = normalized.toLowerCase();

  return normalized;
}

/**
 * Strips the "Descriptor_Unit_" prefix from a descriptor name for search matching.
 * This prevents all units from matching when searching for "Descriptor_Unit".
 * 
 * @param descriptorName The full descriptor name (e.g., "Descriptor_Unit_SomeUnit")
 * @returns The descriptor name without the prefix (e.g., "SomeUnit")
 */
export function getDescriptorSearchText(descriptorName: string): string {
  return descriptorName.replace(/^Descriptor_Unit_/i, '');
}

